import { applyTrustCircleChanges } from './apply-trust-circle-changes';
import { IconArrowLeft, IconCheck, IconSearch } from '@tabler/icons-react-native';
import { userInfoAtom } from 'app/atoms';
import { ShowErrors } from 'app/components/show-errors';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Input } from 'app/design/primitives/input';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { useAddTrustedMember, useRemoveTrustedMember, useTrustCircle } from 'app/network/profile';
import { useFindUsers } from 'app/network/user';
import { useTheme } from 'app/shopify';
import type { TrustMember } from 'app/types/profile';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, SectionList } from 'react-native';

type Member = Pick<TrustMember, 'userId' | 'firstName' | 'lastName' | 'picture'>;

export function TrustCircleManager({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('neighbour-profile');
  const navigation = useNavigation();
  const circle = useTrustCircle();
  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyBase" fontWeight="600">
        {t('trust.manager.title')}
      </Text>
    ),
    headerBackVisible: false,
    headerLeft: () => (
      <Button variant="plain" accessibilityLabel={t('goBack', { ns: 'accessibility' })} onPress={onClose}>
        <IconArrowLeft size={24} strokeWidth={ICON_STROKE_WIDTH} />
      </Button>
    ),
    headerRight: () => null,
  });
  if (!circle.data)
    return (
      <Column padding="spacing.general.x4">
        <ActivityIndicator />
        <ShowErrors error={circle.error} />
      </Column>
    );
  return <TrustCircleEditor members={circle.data} onClose={onClose} />;
}

export function TrustCircleEditor({ members, onClose }: { members: Member[]; onClose: () => void }) {
  const { t } = useTranslation('neighbour-profile');
  const theme = useTheme();
  const viewer = useAtomValue(userInfoAtom);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Record<string, Member>>(() =>
    Object.fromEntries(members.map((m) => [m.userId, m])),
  );
  const [baseline, setBaseline] = useState(() => new Set(members.map((m) => m.userId)));
  const [saving, setSaving] = useState(false);
  const add = useAddTrustedMember();
  const remove = useRemoveTrustedMember();
  const users = useFindUsers({ search: search.trim(), sortBy: 'relevance' });
  const selectedMembers = Object.values(selected);
  const foundMembers: Member[] = (users.data?.pages.flatMap((page) => page.results) ?? []).flatMap((user) =>
    !user.profile || user.id === viewer?.id ? [] : [{ userId: user.id, ...user.profile }],
  );
  const candidates = new Map([...members, ...foundMembers].map((member) => [member.userId, member]));
  const suggestions = [...candidates.values()].filter(
    (member) =>
      !selected[member.userId] &&
      `${member.firstName} ${member.lastName}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const matchingSelected = selectedMembers.filter((member) =>
    `${member.firstName} ${member.lastName}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const dirty =
    baseline.size !== selectedMembers.length || selectedMembers.some((member) => !baseline.has(member.userId));
  const toggle = (member: Member) =>
    setSelected((current) => {
      const next = { ...current };
      if (next[member.userId]) delete next[member.userId];
      else next[member.userId] = member;
      return next;
    });
  const persistChanges = () =>
    applyTrustCircleChanges({
      baseline,
      selectedIds: selectedMembers.map((member) => member.userId),
      add: add.mutateAsync,
      remove: remove.mutateAsync,
      onApplied: (change) =>
        setBaseline((current) => {
          const next = new Set(current);
          if (change.kind === 'remove') next.delete(change.userId);
          else next.add(change.userId);
          return next;
        }),
    });
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await persistChanges();
      onClose();
    } catch {
      // Mutation errors are displayed below; successful changes remain in the retry baseline.
    }
    setSaving(false);
  };
  return (
    <Column flex={1} backgroundColor="surface.default">
      <SectionList
        keyboardShouldPersistTaps="handled"
        stickySectionHeadersEnabled={false}
        contentContainerStyle={{ padding: 16, gap: 8 }}
        sections={[
          { key: 'selected', data: matchingSelected },
          { key: 'suggestions', data: suggestions },
        ]}
        keyExtractor={(member) => member.userId}
        ListHeaderComponent={
          <Input
            label={t('trust.manager.addMembers')}
            placeholder={t('trust.manager.search')}
            accessibilityLabel={t('trust.manager.search')}
            value={search}
            onChangeText={setSearch}
            editable={!saving}
            fieldHeight={48}
            left={<IconSearch size={24} strokeWidth={ICON_STROKE_WIDTH} />}
          />
        }
        renderSectionHeader={({ section }) =>
          section.key === 'selected' ? (
            <Row alignItems="center" justifyContent="space-between" paddingVertical="spacing.general.x2">
              <Text variant="bodyLarge" fontWeight="600">
                {t('trust.memberCount', { count: selectedMembers.length })}
              </Text>
              <Button variant="plain" disabled={saving || !selectedMembers.length} onPress={() => setSelected({})}>
                <Text color="text.emphasized" fontWeight="600">
                  {t('trust.manager.clear')}
                </Text>
              </Button>
            </Row>
          ) : (
            <Text variant="bodyBase" color="text.secondary" style={{ marginVertical: 8 }}>
              {t(search.trim() ? 'trust.manager.results' : 'trust.manager.suggestions')}
            </Text>
          )
        }
        renderItem={({ item }) => {
          const checked = Boolean(selected[item.userId]);
          const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
          return (
            <Pressable
              accessibilityRole="checkbox"
              accessibilityLabel={name}
              accessibilityState={{ checked, disabled: saving }}
              disabled={saving}
              onPress={() => toggle(item)}
              style={{ minHeight: 56, flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <Thumbnail height={48} source={{ uri: item.picture ?? undefined }} />
              <Text flex={1} variant="bodyBase" fontWeight="600">
                {name}
              </Text>
              <Column
                width={24}
                height={24}
                alignItems="center"
                justifyContent="center"
                style={{
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor: theme.colors[checked ? 'surface.brand.default' : 'border.primary'],
                  backgroundColor: checked ? theme.colors['surface.brand.default'] : theme.colors['surface.default'],
                }}
              >
                {checked ? <IconCheck size={20} color={theme.colors['text.inverse.primary']} /> : null}
              </Column>
            </Pressable>
          );
        }}
        onEndReached={() => {
          if (users.hasNextPage && !users.isFetchingNextPage) void users.fetchNextPage();
        }}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          <Column gap="spacing.general.x2">
            {users.isFetching ? <ActivityIndicator /> : null}
            {!users.isFetching && !suggestions.length ? (
              <Text color="text.secondary">{t('trust.manager.noResults')}</Text>
            ) : null}
            <ShowErrors error={users.error ?? add.error ?? remove.error} />
          </Column>
        }
      />
      <Accessory paddingHorizontal="spacing.general.x4" marginTop={16}>
        <Button
          accessibilityRole="button"
          variant="primary"
          loading={saving}
          disabled={!dirty || saving}
          onPress={() => {
            void save();
          }}
          minHeight={56}
          style={{ borderRadius: 1000 }}
        >
          {t('trust.manager.save')}
        </Button>
      </Accessory>
    </Column>
  );
}
